/**
 * Type definitions for special tool results (plan tool) and arguments
 * (ask_user tool)
 */

import type { ClarificationQuestion } from '@/types/artifacts.types';

/** Plan tool result matching backend PlanResponse */
export interface PlanToolResult {
  success: boolean;
  message: string;
  plan_id: string;
  planText: string | null;
  todos?: TodoItemData[];
}

/** Todo item status */
export type TodoStatus = 'pending' | 'in_progress' | 'completed';

/** Single todo item data */
export interface TodoItemData {
  content: string;
  status: TodoStatus;
}

/** Type guard for TodoStatus */
function isValidTodoStatus(status: unknown): status is TodoStatus {
  return (
    typeof status === 'string' &&
    (status === 'pending' ||
      status === 'in_progress' ||
      status === 'completed')
  );
}

/** Type guard for TodoItemData */
function isValidTodoItem(item: unknown): item is TodoItemData {
  if (!item || typeof item !== 'object') return false;
  const obj = item as Record<string, unknown>;
  return (
    typeof obj.content === 'string' && isValidTodoStatus(obj.status)
  );
}

/** Type guard for PlanToolResult */
function isValidPlanToolResult(
  obj: unknown
): obj is PlanToolResult {
  if (!obj || typeof obj !== 'object') return false;
  const parsed = obj as Record<string, unknown>;

  // Validate required fields
  if (
    typeof parsed.success !== 'boolean' ||
    typeof parsed.message !== 'string' ||
    typeof parsed.plan_id !== 'string'
  ) {
    return false;
  }

  // Validate planText (nullable string)
  if (
    parsed.planText !== null &&
    typeof parsed.planText !== 'string'
  ) {
    return false;
  }

  // Validate optional todos array
  if (parsed.todos !== undefined) {
    if (!Array.isArray(parsed.todos)) {
      return false;
    }
    for (const item of parsed.todos) {
      if (!isValidTodoItem(item)) {
        return false;
      }
    }
  }

  return true;
}

/** Parse tool result string to PlanToolResult */
export function parsePlanResult(
  result: unknown
): PlanToolResult | null {
  if (!result) return null;

  try {
    const parsed =
      typeof result === 'string' ? JSON.parse(result) : result;
    if (isValidPlanToolResult(parsed)) {
      return parsed;
    }
  } catch {
    // Not valid JSON
  }
  return null;
}

type ClarificationOption = ClarificationQuestion['options'][number];

const NON_BLANK = /\S/;

// The card adds its own Other row.
const isOtherLabel = (label: string) =>
  label.trim().replace(/:+$/, '').trimEnd().toLowerCase() === 'other';

function parseClarificationOption(item: unknown): ClarificationOption | null {
  if (!item || typeof item !== 'object') return null;
  const { label, description = '' } = item as Record<string, unknown>;
  return typeof label === 'string' &&
    NON_BLANK.test(label) &&
    !isOtherLabel(label) &&
    typeof description === 'string'
    ? { label, description }
    : null;
}

function parseClarificationQuestion(
  item: unknown
): ClarificationQuestion | null {
  if (!item || typeof item !== 'object') return null;
  const {
    question,
    header,
    options,
    multi_select: multiSelect = false,
  } = item as Record<string, unknown>;
  if (
    typeof question !== 'string' ||
    !NON_BLANK.test(question) ||
    typeof header !== 'string' ||
    !NON_BLANK.test(header) ||
    // pydantic counts code points, not UTF-16 units
    Array.from(header).length > 12 ||
    typeof multiSelect !== 'boolean' ||
    !Array.isArray(options) ||
    options.length < 2 ||
    options.length > 4
  ) {
    return null;
  }
  const parsedOptions = options.map(parseClarificationOption);
  return parsedOptions.every(
    (option): option is ClarificationOption => option !== null
  )
    ? { question, header, options: parsedOptions, multi_select: multiSelect }
    : null;
}

/**
 * Questions of an ask_user call, or null when the reasoner's schema would
 * reject its args: 1-4 questions of 2-4 options each; `question`, `header`
 * and `label` hold a non-blank character; `header` has at most 12
 * characters; no label is Other, which the card adds itself. `description`
 * defaults to "" and `multi_select` to false.
 */
export function parseAskUserArgs(
  args: unknown
): ClarificationQuestion[] | null {
  if (!args || typeof args !== 'object') return null;
  const { questions } = args as Record<string, unknown>;
  if (
    !Array.isArray(questions) ||
    questions.length === 0 ||
    questions.length > 4
  ) {
    return null;
  }
  const parsed = questions.map(parseClarificationQuestion);
  return parsed.every(
    (question): question is ClarificationQuestion => question !== null
  )
    ? parsed
    : null;
}

/** A card's answer to one question. */
export interface ClarificationAnswer {
  /** The picked options' indexes. */
  picks: number[];
  /** Text typed as Other, or ''. */
  other: string;
}

/**
 * The body of a reply to a waiting ask_user call; the model reads `text`,
 * which for a card's answer `formatClarificationAnswer` words.
 */
export type AskUserReply =
  | { kind: 'answer'; text: string }
  | { kind: 'skip'; text: '' };

// The reasoner's ask_user results for questions closed without an answer;
// an answer's result is its text verbatim.
export const ASK_USER_SKIPPED_RESULT =
  "[User dismissed the questions — do not proceed, wait for the user's " +
  'next message]';
export const ASK_USER_EXPIRED_RESULT =
  '[No reply arrived in time — the questions expired; wait for the ' +
  "user's next message]";
